import { DbRef } from '@caresend/types';
import { nullishFilter, isNullish, flattenArray } from '@caresend/utils';
import update from 'immutability-helper';
import { unwrapMaybeEncryptedData } from '../../../database/encryption/decrypt.js';
import 'js-sha256';
import '../../../database/firebaseConfig/cloudFunctions.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../database/encryption/model.js';
import 'firebase/auth';
import '../../../database/firebaseConfig/init.js';
import { firebaseBind, firebaseUnbind } from '../../../database/firebaseConfig/methods.js';
import '../../../components/ui/Icon.vue.js';
import '../../../functions/configuration/icons.js';
import '../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../functions/tracking/index.js';
import '../../../router/helpers/initRouter.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import { removeEncryptedPatientID } from './helpers.js';
import { initExtendedModule } from '../helpers.js';

/* eslint-disable max-len */
const bookingsState = {
    allBookingsBound: false,
    allBookingsLoading: false,
    bookings: {},
    bookingsInput: {},
};
const bookingsMutations = {
    /** Used by setOrderDataInStore */
    'bookings/SET_BOOKINGS': (state, newBookings) => {
        var _a;
        state.bookings = update((_a = state.bookings) !== null && _a !== void 0 ? _a : {}, {
            $merge: newBookings,
        });
    },
    'bookings/SET_BOOKING': (state, newBooking) => {
        var _a;
        const bookingID = newBooking.id;
        state.bookings = update((_a = state.bookings) !== null && _a !== void 0 ? _a : {}, {
            [bookingID]: { $set: newBooking },
        });
    },
    'booking/SET_BOOKING_INPUT': (state, newBookingInput) => {
        if (!newBookingInput.id)
            return;
        state.bookingsInput = {
            ...state.bookingsInput,
            [newBookingInput.id]: {
                ...state.bookingsInput[newBookingInput.id],
                ...newBookingInput,
            },
        };
    },
    'bookings/SET_ALL_BOOKINGS_BOUND': (state, newValue) => {
        state.allBookingsBound = newValue;
    },
    'bookings/SET_ALL_BOOKINGS_LOADING': (state, newValue) => {
        state.allBookingsLoading = newValue;
    },
    'bookings/RESET_BOOKINGS': (state) => {
        state.allBookingsBound = false;
        state.allBookingsLoading = false;
        state.bookings = {};
    },
};
const bookingsActions = {
    'bookings/bindAllBookings': async ({ commit, dispatch }) => {
        const path = DbRef.BOOKINGS;
        await firebaseBind(path, async (bookings) => {
            commit('bookings/SET_ALL_BOOKINGS_LOADING', true);
            const decryptedBookings = {};
            const userIDs = [];
            const promises = Object.entries(bookings).map(async ([bookingID, booking]) => {
                if (!booking.id)
                    return;
                const patientID = await unwrapMaybeEncryptedData(booking.patientID);
                const newBooking = {
                    ...booking,
                    patientID,
                };
                if (patientID)
                    userIDs.push(patientID);
                decryptedBookings[bookingID] = newBooking;
            });
            await Promise.all(promises);
            dispatch('users/fetchUsers', userIDs);
            commit('bookings/SET_BOOKINGS', decryptedBookings);
            commit('bookings/SET_ALL_BOOKINGS_BOUND', true);
            commit('bookings/SET_ALL_BOOKINGS_LOADING', false);
        });
        return true;
    },
    'bookings/unbindAllBookings': async ({ commit }) => {
        const path = `${DbRef.BOOKINGS}`;
        await firebaseUnbind(path);
        commit('bookings/SET_ALL_BOOKINGS_BOUND', false);
        return true;
    },
    'bookings/fetchBookingForViewing': async ({ commit, dispatch }, { bookingID, skipUserFetches }) => {
        const userIDs = [];
        const bookingPath = `${DbRef.BOOKINGS}/${bookingID}`;
        await firebaseBind(bookingPath, async (booking) => {
            if (!booking) {
                firebaseUnbind(bookingPath);
                return;
            }
            if (booking.orderID) {
                const orderUserIDsObj = await dispatch('orders/bindOrder', { orderID: booking.orderID, skipUserFetches });
                userIDs.push(...orderUserIDsObj.userIDs);
            }
            const unencryptedBooking = removeEncryptedPatientID(booking);
            commit('bookings/SET_BOOKING', unencryptedBooking);
            const patientID = await unwrapMaybeEncryptedData(booking.patientID);
            const newBooking = {
                ...booking,
                patientID,
            };
            if (patientID) {
                userIDs.push(patientID);
                if (!skipUserFetches)
                    await dispatch('users/fetchUser', patientID);
            }
            commit('bookings/SET_BOOKING', newBooking);
        });
        return { userIDs };
    },
};
const bookingsGetters = {
    'booking/getBookingInputByID': (state) => (bookingID) => {
        var _a;
        if (!bookingID)
            return undefined;
        return (_a = state.bookingsInput[bookingID]) !== null && _a !== void 0 ? _a : undefined;
    },
    'bookings/getBookingsOnOrderById': (state) => (orderID) => Object.values(state.bookings)
        .filter((booking) => booking.orderID === orderID)
        .map((booking) => booking.id),
    'bookings/getBookingOrderID': (state) => (bookingID) => { var _a, _b; return (_b = (_a = state.bookings) === null || _a === void 0 ? void 0 : _a[bookingID]) === null || _b === void 0 ? void 0 : _b.orderID; },
    'bookings/getPatientIDs': (state) => (bookingIDs) => {
        const bookings = Object.values(state.bookings)
            .filter((booking) => bookingIDs.includes(booking.id));
        const patientIDs = bookings.map((booking) => booking.patientID).filter(nullishFilter);
        return patientIDs;
    },
    'bookings/getPatientOnBookingByID': (state, _getters, rootState) => (bookingID) => {
        const booking = state.bookings[bookingID];
        if (isNullish(booking))
            return;
        const { patientID } = booking;
        return rootState.users.users[patientID];
    },
    'bookings/getProceduresOnBookingsByID': (state, _getters, rootState) => (bookingIDs) => {
        if (!bookingIDs)
            return null;
        const bookingProcedures = bookingIDs.map((bookingID) => {
            var _a, _b;
            const bookingProcedureReferences = Object.values((_b = (_a = state.bookings[bookingID]) === null || _a === void 0 ? void 0 : _a.procedures) !== null && _b !== void 0 ? _b : {});
            const procedures = bookingProcedureReferences.map((procedure) => rootState.procedures.procedures[procedure.id]);
            return procedures.filter(nullishFilter);
        });
        return flattenArray(bookingProcedures);
    },
};
const bookingsModuleExtension = {
    state: bookingsState,
    mutations: bookingsMutations,
    actions: bookingsActions,
    getters: bookingsGetters,
};
const initBookingsModule = (moduleExtensions) => initExtendedModule(bookingsModuleExtension, moduleExtensions);

export { initBookingsModule };
